import React, { useRef, useEffect, useState } from 'react';
import Button from './button';
import { consts } from '../consts';

interface CameraCaptureProps {
  onCapture: (imageDataUrl: string) => void;
  onCancel: () => void;
}

const CameraCapture: React.FC<CameraCaptureProps> = ({ onCapture, onCancel }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mediaStream: MediaStream | null = null;

    const startCamera = async () => {
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { 
            facingMode: 'user',
            width: { ideal: consts.avatar.width },
            height: { ideal: consts.avatar.height }
          }
        });
        
        setStream(mediaStream);
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
        setIsLoading(false);
      } catch (err) {
        console.error('Error accessing camera:', err);
        setError('Unable to access camera. Please ensure camera permissions are granted.');
        setIsLoading(false);
      }
    };

    startCamera();

    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const handleCapture = () => {
    if (!videoRef.current) {
      setError('Video not available');
      return;
    }

    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    
    // Set canvas to square dimensions
    const size = Math.min(video.videoWidth, video.videoHeight);
    canvas.width = consts.avatar.width;
    canvas.height = consts.avatar.height;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setError('Unable to process image');
      return;
    }

    // Calculate the center crop
    const sx = (video.videoWidth - size) / 2;
    const sy = (video.videoHeight - size) / 2;
    
    // Draw the cropped video frame
    ctx.drawImage(video, sx, sy, size, size, 0, 0, consts.avatar.width, consts.avatar.height);
    
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
    
    // Stop the camera
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    
    onCapture(imageDataUrl);
  };

  return (
    <div className="camera-capture-overlay">
      <div className="bg-[var(--bg-primary)] rounded-2xl p-6 max-w-[500px] w-[90%] max-h-[90vh] overflow-y-auto">
        <h2 className="camera-capture-title">Take a Selfie</h2>
        
        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-center">
            {error}
          </div>
        )}
        
        {isLoading && !error && (
          <div className="camera-capture-loading">
            Loading camera...
          </div>
        )}
        
        <div className="relative w-full aspect-square mb-4 rounded-lg overflow-hidden bg-black">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
          <div className="camera-capture-frame">
            <div className="camera-capture-frame-corner camera-capture-frame-corner-tl"></div>
            <div className="absolute w-[30px] h-[30px] border-3 border-white shadow-lg top-0 right-0 border-l-0 border-b-0"></div>
            <div className="camera-capture-frame-corner camera-capture-frame-corner-bl"></div>
            <div className="absolute w-[30px] h-[30px] border-3 border-white shadow-lg bottom-0 right-0 border-l-0 border-t-0"></div>
          </div>
        </div>
        
        <div className="flex gap-3 justify-center">
          <Button onClick={onCancel} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleCapture} disabled={!!error || isLoading}>
            Capture
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CameraCapture;
